import van, { Van } from "vanjs-core"
import parse from "html-dom-parser"
import { ChildNode, Element, Text } from "domhandler"

const dummy = "DUMMY"

const quoteIfNeeded = (key: string) => /^[a-zA-Z_][a-zA-Z_0-9]+$/.test(key) ? key : `"${key}"`

type Dom = Element | Text

const filterDoms = (doms: readonly ChildNode[], skipEmptyText: boolean) =>
  <Dom[]>doms.filter(
    c => c.type === "tag" && c.name !== dummy ||
      c.type === "text" && (!skipEmptyText || /\S/.test(c.data)) ||
      c.type === "script")

export interface HtmlToVanCodeOptions {
  indent?: number
  spacing?: boolean
  skipEmptyText?: boolean
  htmlTagPred?: (name: string) => boolean
}

export const htmlToVanCode = (html: string, {
  indent = 2,
  spacing = false,
  skipEmptyText = false,
  htmlTagPred = s => s.toLowerCase() === s,
}: HtmlToVanCodeOptions = {}) => {
  const attrsToVanCode = (attrs: Record<string, string>) => {
    const space = spacing ? " " : ""
    return Object.keys(attrs).length === 0 ? "" :
      `, {${space}${Object.entries(attrs)
        .flatMap(([k, v]) => k !== dummy ? `${quoteIfNeeded(k)}: ${JSON.stringify(v)}` : [])
        .join(", ")}${space}}`
  }

  const domsToVanCode = (
    doms: readonly Dom[], prefix: string, skipEmptyText: boolean, tagsUsed: Set<string>,
  ) => doms.flatMap((dom): string | string[] => {
    const suffix = !prefix && doms.length <= 1 ? "" : ","
    if(dom.type === "text") return `${prefix}$t(${JSON.stringify(dom.data)})${suffix}`
    tagsUsed.add(dom.name)
    const localSkipEmptyText = skipEmptyText && dom.name !== "pre"

    let children = filterDoms(dom.children, localSkipEmptyText)

    const otherAttribs = { ...dom.attribs };
    delete otherAttribs.class;
    delete otherAttribs.id;
    const classList = dom.attribs.class ? dom.attribs.class.split(" ") : [];
    const attrsCode = attrsToVanCode(otherAttribs);
    let childText = "";
    if(children.every(c => c.type === "text")) {
      childText = children.map(c => c.type === "text" && c.data).join("");
      children = [];
    }

    const childrenCode = domsToVanCode(children, prefix + " ".repeat(indent), localSkipEmptyText, tagsUsed);
    const tagNameSuffix = `${dom.attribs.id ? "#" + dom.attribs.id : ""}${classList.map(c => "." + c).join("")}`;
    const callStart = `$h("${!tagNameSuffix || dom.name !== "div" ? dom.name : ""}${tagNameSuffix}"${attrsCode}`;

    return children.length > 0 ? [
      `${prefix}${callStart}, [`,
      ...childrenCode,
      `${prefix}])${suffix}`,
    ] : childText ? [
      `${prefix}${callStart}, ${JSON.stringify(childText)})${suffix}`,
    ] : `${prefix}${callStart})${suffix}`
  })

  const doms = parse(html, <any>{ lowerCaseTags: false, lowerCaseAttributeNames: false })
  const tagsUsed = new Set<string>
  const code = domsToVanCode(filterDoms(doms, skipEmptyText), "", skipEmptyText, tagsUsed)
  const tags: string[] = [], components: string[] = []
  for(const tag of tagsUsed) (htmlTagPred(tag) ? tags : components).push(tag)
  return { code, tags: tags.sort(), components: components.sort() }
}

function stripJquery(html: string) {
  return html.replace(/^\$\((["'`])(.+)\1\)$/s, "$2");
}

declare global {
  interface Window { van: Van, Prism: any }
}

const { state, derive, add, tags: { code, div, h5, input, option, pre, select, span, textarea } } = van;

const autogrow = (dom: HTMLTextAreaElement) => {
  dom.style.height = "5px"
  dom.style.height = (dom.scrollHeight + 5) + "px"
}

const Converter = () => {
  const inputType = state("HTML")
  const isHtml = derive(() => inputType.val === "HTML")

  const htmlInput = state('<div><p>👋Hello</p><ul><li>🗺️World</li><li><a href="https://vanjs.org/">🍦VanJS</a></li></ul></div>')
  const mdInput = state(`👋Hello
* 🗺️World
* [🍦VanJS](https://vanjs.org/)
`)
  const currentInput = derive(() => (isHtml.val ? htmlInput : mdInput).val)

  const inputDom = textarea({
    style: "width: 100%;",
    oninput: e => (isHtml.val ? htmlInput : mdInput).val = e.target.value,
    value: currentInput,
  })
  derive(() => (currentInput.val, setTimeout(autogrow.bind(null, inputDom), 5)))

  const indent = state(2), spacing = state(true), skipEmpty = state(true)
  const err = state("")

  const result = derive(() => {
    err.val = ""
    try {
      return htmlToVanCode(stripJquery(htmlInput.val),
        { indent: indent.val, spacing: spacing.val, skipEmptyText: skipEmpty.val })
    } catch(e) {
      err.val = (<any>e).toString()
    }
  })

  const showForHtml = () => isHtml.val ? "" : "display: none"

  return div(
    h5(
      "Paste your HTML snippet here:",
    ),
    div(inputDom),
    div(
      "Indent level: ", input({
        type: "number", min: 1, max: 8, value: indent,
        oninput: e => indent.val = e.target.value
      }), " ",
      input({ type: "checkbox", checked: spacing, oninput: e => spacing.val = e.target.checked }),
      "Spacing ",
      span({ style: showForHtml },
        input({ type: "checkbox", checked: skipEmpty, oninput: e => skipEmpty.val = e.target.checked }),
        "Skip empty text "
      ),
    ),
    pre({ class: "err" }, err),
    (dom?: Element) => {
      let preDom;
      if(!result.val) return div()
      if(!dom?.hasChildNodes())
        dom = div(
          h5("Building the DOM tree:"),
          preDom = pre({
            onclick: e => {
              const selection = window.getSelection();
              if(!selection)
                return;
              e.preventDefault();
              const range = document.createRange();
              range.selectNodeContents(preDom);
              selection.removeAllRanges();
              selection.addRange(range);
            }
          }, code({ class: "language-js" })));

      const { code: lines, tags } = result.val
      const space = spacing.val ? " " : ""
      const [codeDom] = dom.querySelectorAll("pre code[class='language-js']")
      codeDom.textContent = lines.map(l => l + "\n").join("")

      return dom
    }
  )
}

add(document.getElementById("converter")!, Converter())
